// Lib
import React, { useCallback, useMemo } from 'react';
import { Box, Checkbox, Stack, Tooltip, Typography } from '@mui/material';

// Components
import Button from '../../Button';
import Feather from '../../Feather';
import AddressCardHeader from './AddressCardHeader/AddressCardHeader';
import { ReactComponent as PinImg } from '../../../assets/svg/pin.svg';
import { ReactComponent as PinSmallImg } from '../../../assets/svg/pinSmall.svg';

// Hooks
import useAddress from '../../../pages/AddressBook/hooks/useAddress';
import useUser from '../../../hooks/useUser';

// Constants
import { ADDRESS_CARD_HEIGHTS } from '../../../styles/constants';
import useUI from '../../../hooks/useUI';

// Styles
import { useStyles } from './CardStyles';

const Card = ({ address, isListView, isAddressBookPage }) => {
  const { isMobileMode, isDesktopMode } = useUI();
  const {
    onDeleteAddress,
    onMakeMainAddress,
    onEditAddress,
    checkedAsShippingAddress,
    onToggleCheckbox,
    withSelectionCheckbox,
  } = useAddress(address);
  const fullAddress = address.fullAddress || address.full_address;
  const { isKiteUser } = useUser();

  const height = useMemo(() => {
    let device = 'tablet';
    if (isDesktopMode) device = 'desktop';
    if (isMobileMode) device = 'mobile';
    const page = isAddressBookPage ? 'addressBookPage' : 'cartPage';
    const view = isListView ? 'listView' : 'gridView';

    return ADDRESS_CARD_HEIGHTS[device][page][view];
  }, [isDesktopMode, isMobileMode, isListView, isAddressBookPage]);

  const { classes, cx } = useStyles({
    height,
    checkedAsShippingAddress,
    isKiteUser,
    isAddressBookPage,
    isListView,
  });

  const renderCheckbox = useCallback(() => {
    if (!withSelectionCheckbox) {
      return (
        <Tooltip
          enterTouchDelay={0}
          classes={{
            popper: classes.popper,
          }}
          disableFocusListener
          title="Please Select Either Domestic Or International Addresses.">
          <Feather className={classes.warning} type="warning" />
        </Tooltip>
      );
    }
    return (
      <Checkbox
        className={classes.checkbox}
        onClick={onToggleCheckbox}
        checked={checkedAsShippingAddress}
        data-testid="address-card-checkbox"
      />
    );
  }, [
    withSelectionCheckbox,
    classes,
    onToggleCheckbox,
    checkedAsShippingAddress,
  ]);

  const renderAddressBookPageButtons = () => {
    if (isAddressBookPage) {
      return (
        <div className={classes.buttons}>
          <Button onClick={onEditAddress} variant="text">
            <Feather type="edit" />
          </Button>
          <Button variant="text" onClick={onDeleteAddress}>
            <Feather type="delete" />
          </Button>
          <Button
            variant="text"
            onClick={onMakeMainAddress}
            className={
              address.is_main
                ? cx(classes.mainAddress, classes.checkedMainAddress)
                : classes.mainAddress
            }>
            <PinImg
              className={address.is_main ? classes.mainPinImg : classes.pinImg}
            />
            {isListView && (
              <Typography
                ml={0.5}
                variant="textSm"
                color={address.is_main && '#FFFFFF'}>
                {address.is_main ? 'Default Address' : 'Make Default Address'}
              </Typography>
            )}
          </Button>
        </div>
      );
    }
  };

  return (
    <div className={classes.root}>
      <div className={classes.container}>
        <Box
          display="flex"
          justifyContent={
            !isAddressBookPage && !address.is_main ? 'end' : 'space-between'
          }>
          {renderAddressBookPageButtons()}
          {!isAddressBookPage && address.is_main && (
            <Stack
              direction="row"
              alignItems="center"
              columnGap={0.5}
              className={classes.cartMainAddress}>
              <PinSmallImg />
              <Typography variant="textSm">Default Address</Typography>
            </Stack>
          )}
          {!isAddressBookPage && renderCheckbox()}
        </Box>
        <AddressCardHeader
          address={address}
          isListView
          isAddressBookPage={isAddressBookPage}
        />
        {fullAddress && (
          <div
            className={classes.fullAddress}
            data-testid="address-card-full-address">
            {fullAddress}
          </div>
        )}
      </div>
    </div>
  );
};

export default Card;
